import React, { useEffect, useState } from "react";
import { Filter, X, ChevronDown, Search, Truck, CreditCard, User, Home, Calendar, Clock, Sliders, Banknote } from "lucide-react";
import { callApi } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { CommonSelect } from "@/components/common/CommonSelect";
import { MultiSelect } from "@/components/common/MultiSelect";
import DateRangePicker from "@/components/common/DateRange";
import { Range } from "react-date-range";

interface OrderAdvancedFiltersProps {
    filters: any;
    updateFilter: (newFilters: Partial<any>) => void;
    resetFilters: () => void;
    isAdmin: boolean;
    dateRange: Range[];
    setDateRange: React.Dispatch<React.SetStateAction<Range[]>>;
}

export const OrderAdvancedFilters: React.FC<OrderAdvancedFiltersProps> = ({
    filters,
    updateFilter,
    resetFilters,
    isAdmin,
    dateRange,
    setDateRange,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [stores, setStores] = useState<any[]>([]);
    const [loadingStores, setLoadingStores] = useState(false);

    const fetchStores = async (search = "") => {
        if (!isAdmin) return;
        setLoadingStores(true);
        try {
            const response = await callApi<any>(
                `${API_URL}${API_ENDPOINT.stores}/all?limit=100${search ? `&search=${search}` : ""}`,
                { method: 'GET' }
            );
            setStores(response?.data?.stores || response?.data || []);
        } catch (error) {
            console.error("Failed to fetch Stores:", error);
        } finally {
            setLoadingStores(false);
        }
    };

    useEffect(() => {
        if (isExpanded && isAdmin) {
            fetchStores();
        }
    }, [isExpanded, isAdmin]);

    const hasActiveFilters = Object.keys(filters).some(key => {
        if (['page', 'limit', 'sort', 'search'].includes(key)) return false;
        const val = filters[key];
        if (Array.isArray(val)) return val.length > 0;
        return !!val;
    }) || dateRange[0]?.startDate !== undefined || dateRange[0]?.endDate !== undefined;

    const handleClearAll = () => {
        setDateRange([
            {
                startDate: undefined,
                endDate: undefined,
                key: 'selection',
            },
        ]);
        resetFilters();
    };

    return (
        <div className="bg-card rounded-2xl border border-secondary/20 overflow-hidden transition-all duration-300 shadow-sm">
            {/* Header / Basic Search */}
            <div className="p-4 flex flex-wrap items-center gap-4">
                <div className="relative flex-1 min-w-[280px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Search orders by ID or customer..."
                        className="w-full pl-10 pr-4 py-2 bg-secondary/5 border border-secondary/15 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                        value={filters.search || ""}
                        onChange={(e) => updateFilter({ search: e.target.value })}
                    />
                </div>

                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className={`
                            flex items-center gap-2.5 px-4 h-10 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer
                            ${isExpanded
                                ? 'bg-primary text-white shadow-lg shadow-primary/25 border-transparent'
                                : 'bg-secondary/10 text-foreground border border-secondary/20 hover:bg-secondary/20 hover:border-secondary/30'
                            }
                        `}
                    >
                        <Filter className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-180 scale-110' : ''}`} />
                        <span>Advanced Filters</span>
                        <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                            <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                        </div>
                    </button>

                    {hasActiveFilters && (
                        <button
                            type="button"
                            onClick={handleClearAll}
                            className="flex items-center gap-1.5 px-4 h-10 rounded-xl text-sm font-semibold text-destructive hover:bg-destructive/10 transition-all duration-200 cursor-pointer border border-transparent hover:border-destructive/10 active:scale-95"
                        >
                            <X className="w-4 h-4" />
                            <span>Clear All</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Expanded Filters */}
            {isExpanded && (
                <div className="p-5 border-t border-secondary/15 bg-secondary/5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 animate-in slide-in-from-top-2 duration-300">

                    {/* Order Status */}
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <Clock className="w-3 h-3" /> Order Status
                        </label>
                        <CommonSelect
                            value={filters.status}
                            onValueChange={(val) => updateFilter({ status: val, page: 1 })}
                            options={[
                                // { label: "All Status", value: "" },
                                { label: "Pending", value: "pending" },
                                { label: "Accepted", value: "accepted" },
                                { label: "In Progress", value: "in_progress" },
                                { label: "Assigned", value: "assigned" },
                                { label: "Arrived", value: "arrived" },
                                { label: "Ready for Pickup", value: "ready_for_pickup" },
                                { label: "Picked Up", value: "picked_up" },
                                { label: "Out for Delivery", value: "out_for_delivery" },
                                { label: "Destination Arrived", value: "destination_arrived" },
                                { label: "Delivered", value: "delivered" },
                                { label: "Completed", value: "completed" },
                                { label: "Delayed", value: "delayed" },
                                { label: "Cancelled", value: "cancelled" },
                                { label: "Failed", value: "failed" },
                                { label: "Refund Requested", value: "refund_requested" },
                                { label: "Refunded", value: "refunded" },
                                { label: "Refund Rejected", value: "refund_rejected" },
                            ]}
                            placeholder="Select Status"
                            triggerClassName="w-full h-10 rounded-xl bg-card border-secondary/20 font-medium"
                        />
                    </div>

                    {/* Delivery Type */}
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <Truck className="w-3 h-3" /> Delivery Type
                        </label>
                        <CommonSelect
                            value={filters.deliveryType}
                            onValueChange={(val) => updateFilter({ deliveryType: val, page: 1 })}
                            options={[
                                // { label: "All Types", value: "" },
                                { label: "Delivery", value: "delivery" },
                                { label: "Takeaway", value: "takeaway" },
                                { label: "Courier", value: "courier" },
                            ]}
                            placeholder="Select Delivery Type"
                            triggerClassName="w-full h-10 rounded-xl bg-card border-secondary/20 font-medium"
                        />
                    </div>

                    {/* Delivery Status */}
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <Sliders className="w-3 h-3" /> Delivery Status
                        </label>
                        <CommonSelect
                            value={filters.deliveryStatus}
                            onValueChange={(val) => updateFilter({ deliveryStatus: val, page: 1 })}
                            options={[
                                // { label: "All Delivery Status", value: "" },
                                { label: "On Time", value: "on_time" },
                                { label: "Delayed", value: "delayed" },
                            ]}
                            placeholder="Select Delivery Status"
                            triggerClassName="w-full h-10 rounded-xl bg-card border-secondary/20 font-medium"
                        />
                    </div>

                    {/* Payment Method */}
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <CreditCard className="w-3 h-3" /> Payment Method
                        </label>
                        <CommonSelect
                            value={filters.paymentMethod}
                            onValueChange={(val) => updateFilter({ paymentMethod: val, page: 1 })}
                            options={[
                                // { label: "All Methods", value: "" },
                                { label: "Card", value: "card" },
                                { label: "Wallet", value: "wallet" },
                                { label: "COD (Cash on Delivery)", value: "cod" },
                            ]}
                            placeholder="Select Payment Method"
                            triggerClassName="w-full h-10 rounded-xl bg-card border-secondary/20 font-medium"
                        />
                    </div>

                    {/* Payment Status */}
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <CreditCard className="w-3 h-3" /> Payment Status
                        </label>
                        <CommonSelect
                            value={filters.paymentStatus}
                            onValueChange={(val) => updateFilter({ paymentStatus: val, page: 1 })}
                            options={[
                                // { label: "All Payment Status", value: "" },
                                { label: "Pending", value: "pending" },
                                { label: "Paid", value: "paid" },
                                { label: "Unpaid", value: "unpaid" },
                                { label: "Refunded", value: "refunded" },
                                { label: "Failed", value: "failed" },
                                { label: "Partial Paid", value: "partial_paid" },
                            ]}
                            placeholder="Select Payment Status"
                            triggerClassName="w-full h-10 rounded-xl bg-card border-secondary/20 font-medium"
                        />
                    </div>

                    {/* Store Settlement */}
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <Banknote className="w-3 h-3" /> Store Settlement
                        </label>
                        <CommonSelect
                            value={filters.storeSettlementStatus}
                            onValueChange={(val) => updateFilter({ storeSettlementStatus: val, page: 1 })}
                            options={[
                                { label: "Pending", value: "pending" },
                                { label: "Completed", value: "completed" },
                                { label: "Rejected", value: "rejected" },
                            ]}
                            placeholder="Select Settlement Status"
                            triggerClassName="w-full h-10 rounded-xl bg-card border-secondary/20 font-medium"
                        />
                    </div>

                    {/* Driver Status */}
                    {/* <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <User className="w-3 h-3" /> Driver Status
                        </label>
                        <CommonSelect
                            value={filters.driverStatus || "all"}
                            onValueChange={(val) => updateFilter({ driverStatus: val === "all" ? "" : val, page: 1 })}
                            options={[
                                { label: "All Driver Status", value: "all" },
                                { label: "Pending", value: "pending" },
                                { label: "Accepted", value: "accepted" },
                                { label: "Assigned", value: "assigned" },
                                { label: "Arrived", value: "arrived" },
                                { label: "Out for Delivery", value: "out_for_delivery" },
                                { label: "Destination Arrived", value: "destination_arrived" },
                                { label: "Delivered", value: "delivered" },
                                { label: "Delayed", value: "delayed" },
                                { label: "Cancelled", value: "cancelled" },
                                { label: "Failed", value: "failed" },
                                { label: "Refund Requested", value: "refund_requested" },
                                { label: "Refunded", value: "refunded" },
                                { label: "Refund Rejected", value: "refund_rejected" },
                            ]}
                            triggerClassName="w-full h-10 rounded-xl bg-card border-secondary/20 font-medium"
                        />
                    </div> */}



                    {/* Store (Admin only) */}
                    {isAdmin && (
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                                <Home className="w-3 h-3" />Store
                            </label>
                            <MultiSelect
                                options={stores.map(storeData => ({ label: storeData.name || storeData.fullName || "Unnamed Store", value: storeData.id }))}
                                selected={filters.storeId ? [filters.storeId] : []}
                                onChange={(vals) => updateFilter({ storeId: vals[0] || "", page: 1 })}
                                onSearchChange={fetchStores}
                                placeholder="Select Stores"
                                singleSelect
                                loading={loadingStores}
                            />
                        </div>
                    )}

                    {/* User ID */}
                    {/* <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <User className="w-3 h-3" /> Customer/User ID
                        </label>
                        <input
                            type="text"
                            placeholder="Filter by Customer ID..."
                            className="w-full px-3 py-2 bg-card border border-secondary/20 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
                            value={filters.userId || ""}
                            onChange={(e) => updateFilter({ userId: e.target.value, page: 1 })}
                        />
                    </div> */}

                    {/* Driver ID */}
                    {/* <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <User className="w-3 h-3" /> Driver ID
                        </label>
                        <input
                            type="text"
                            placeholder="Filter by Driver ID..."
                            className="w-full px-3 py-2 bg-card border border-secondary/20 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
                            value={filters.driverId || ""}
                            onChange={(e) => updateFilter({ driverId: e.target.value, page: 1 })}
                        />
                    </div> */}

                    {/* Date Range Picker */}
                    <div className="space-y-1.5 ">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <Calendar className="w-3 h-3" /> Date Range
                        </label>
                        <div className="flex items-center gap-2">
                            <DateRangePicker
                                dateRange={dateRange}
                                setDateRange={setDateRange}
                            />
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
};
